import get from 'lodash/get';
import moment from 'moment';
import config from '../config';
import routeConfiguration from '../routeConfiguration';
import { createResourceLocatorString } from './routes';
import { createSlug } from './urlHelpers';
import { stripHtml } from './sanitizeHtml';
import {
  getListingImages,
  getListingSchemaTitleType,
  getLodgingPricesOptions,
  getPackageImages,
} from './listing';
import {
  checkPackageAvailability,
  getFlexiblePricingData,
  getPackageLodgingPrices,
  getStartingPrice,
  LODGING_INFO,
} from './package';

const SCHEMA_CONTEXT = 'https://schema.org';
const ORGANIZATION_ID = `${config.canonicalRootURL}#organization`;

const ACTIVITY_LABELS = {
  hunt: 'Hunting',
  outdoor_recreation: 'Outdoor recreation',
  hunt_diy: 'DIY hunting',
  fish: 'Fishing',
  bird: 'Birdwatching',
  farm: 'Farm and ranch experiences',
};

const isFiniteNumber = value =>
  value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

const compactObject = object =>
  Object.entries(object).reduce((result, [key, value]) => {
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    if (value !== null && value !== undefined && value !== '' && !isEmptyArray) {
      result[key] = value;
    }
    return result;
  }, {});

const activityLabel = activity => {
  if (!activity) return null;
  if (ACTIVITY_LABELS[activity]) return ACTIVITY_LABELS[activity];

  return activity
    .split('_')
    .filter(Boolean)
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

const imageUrl = image => {
  const variants = get(image, 'attributes.variants', {});
  const sizes = get(image, 'attributes.sizes', []);
  const legacyFacebook = sizes.find(size => size.name === 'facebook');

  return (
    get(variants, 'facebook.url') ||
    get(variants, 'scaled-large.url') ||
    get(variants, 'default.url') ||
    get(legacyFacebook, 'url') ||
    null
  );
};

const imageUrls = (images, limit) => {
  const urls = (images || []).map(imageUrl).filter(Boolean);
  const uniqueUrls = [...new Set(urls)];
  return limit ? uniqueUrls.slice(0, limit) : uniqueUrls;
};

const listingUrl = listing => {
  const id = get(listing, 'id.uuid');
  const title = get(listing, 'attributes.title', '');
  if (!id) return null;

  return `${config.canonicalRootURL}${createResourceLocatorString(
    'ListingPage',
    routeConfiguration(),
    { id, slug: createSlug(title) },
    {}
  )}`;
};

const packageUrl = (listing, packageItem) => {
  const id = get(listing, 'id.uuid');
  const title = get(listing, 'attributes.title', '');
  const packageId = packageItem && packageItem.id;
  if (!id || !packageId) return null;

  return `${config.canonicalRootURL}${createResourceLocatorString(
    'PackagePage',
    routeConfiguration(),
    { id, slug: createSlug(title), packageId },
    {}
  )}`;
};

const postalAddress = listing => {
  const publicData = get(listing, 'attributes.publicData', {});
  const nearestTown = get(publicData, 'nearestTown.selectedPlace.address');
  const addressLocality = nearestTown ? nearestTown.split(',')[0].trim() : null;
  const addressRegion = get(publicData, 'location.usState');

  if (!addressLocality && !addressRegion) return null;

  return compactObject({
    '@type': 'PostalAddress',
    addressLocality,
    addressRegion,
    addressCountry: addressRegion ? 'US' : null,
  });
};

const propertyValue = (name, value, extra = {}) =>
  compactObject({
    '@type': 'PropertyValue',
    name,
    value,
    ...extra,
  });

const rangeProperty = (name, range, unitText) => {
  const minValue =
    isFiniteNumber(range && range.min) && Number(range.min) > 0 ? Number(range.min) : null;
  const maxValue =
    isFiniteNumber(range && range.max) && Number(range.max) > 0 ? Number(range.max) : null;

  if (!minValue && !maxValue) return null;
  if (minValue && maxValue && minValue === maxValue) {
    return propertyValue(name, minValue, { unitText });
  }

  return compactObject({
    '@type': 'PropertyValue',
    name,
    minValue,
    maxValue,
    unitText,
  });
};

const lodgingStatus = packageItem => {
  const lodgingPrices = get(packageItem, 'lodgingPrices', []);
  const hasRequired = lodgingPrices.some(item => item.option === LODGING_INFO.REQUIRED);
  const hasOptional = lodgingPrices.some(item => item.option === LODGING_INFO.OPTIONAL);

  if (hasRequired && hasOptional) return 'Included; optional lodging upgrades available';
  if (hasRequired) return 'Included';
  if (hasOptional) return 'Optional add-on';

  const legacyStatus = packageItem && packageItem.hasLodging;
  if (legacyStatus === LODGING_INFO.REQUIRED) return 'Included';
  if (legacyStatus === LODGING_INFO.OPTIONAL) return 'Optional add-on';
  if (legacyStatus === LODGING_INFO.NOT_OFFERED || legacyStatus === false) return 'Not offered';

  return null;
};

const startingPrice = (listing, packageItem, intl) => {
  if (!packageItem || !packageItem.price) return null;

  try {
    const lodgingPricesOptions = getLodgingPricesOptions(listing, intl);
    const { optionalLodging, includedLodging } = getPackageLodgingPrices(
      packageItem,
      lodgingPricesOptions
    );
    const minParty = get(packageItem, 'guests.min', 1);
    const flexiblePricing = getFlexiblePricingData(packageItem.flexiblePricing, minParty);

    return getStartingPrice({
      ...packageItem,
      optionalLodging,
      includedLodging,
      flexiblePricing,
    });
  } catch (e) {
    return null;
  }
};

const validDate = value => {
  const date = value ? moment.utc(value, moment.ISO_8601, true) : null;
  return date && date.isValid() ? date.format('YYYY-MM-DD') : null;
};

// Uses the same check as the booking flow. A package outside its booking
// dates or out of spots can't be booked right now, so it's OutOfStock. We
// don't use SoldOut (most are seasonal packages with last year's dates) or
// PreOrder (it means you can book now, which the booking flow blocks).
const offerAvailability = (packageItem, now) => {
  const { bookingRangeIsFuture, bookingRangeIsPast, isAvailable } = checkPackageAvailability(
    packageItem,
    now
  );

  return isAvailable && !bookingRangeIsFuture && !bookingRangeIsPast
    ? `${SCHEMA_CONTEXT}/InStock`
    : `${SCHEMA_CONTEXT}/OutOfStock`;
};

const eligibleQuantity = packageItem => {
  const guests = packageItem && packageItem.guests;
  if (!guests) return null;

  const hasMinValue = isFiniteNumber(guests.min) && Number(guests.min) >= 0;
  const hasMaxValue = isFiniteNumber(guests.max) && Number(guests.max) >= 0;
  if (!hasMinValue && !hasMaxValue) return null;

  const minValue = hasMinValue ? Math.max(Number(guests.min), 1) : null;
  const maxValue = hasMaxValue ? Math.max(Number(guests.max), 1) : null;

  return compactObject({
    '@type': 'QuantitativeValue',
    minValue,
    maxValue,
    unitText: 'guests',
  });
};

const buildOffer = ({
  listing,
  packageItem,
  intl,
  includeItemOffered = false,
  requirePrice = false,
  now,
}) => {
  const listingCanonicalUrl = listingUrl(listing);
  const packageCanonicalUrl = packageUrl(listing, packageItem);
  if (!listingCanonicalUrl || !packageCanonicalUrl) return null;

  const price = startingPrice(listing, packageItem, intl);
  const priceAmount =
    price && isFiniteNumber(price.amount) ? Number((price.amount / 100).toFixed(2)) : null;
  const priceCurrency = price && price.currency;
  if (requirePrice && (priceAmount === null || !priceCurrency)) return null;
  const offerId = `${packageCanonicalUrl}#offer`;
  const productId = `${packageCanonicalUrl}#product`;
  const businessId = `${listingCanonicalUrl}#business`;

  return compactObject({
    '@type': 'Offer',
    '@id': offerId,
    url: packageCanonicalUrl,
    price: priceAmount,
    priceCurrency,
    // The page shows this starting price "per Guest".
    priceSpecification:
      priceAmount === null
        ? null
        : {
            '@type': 'UnitPriceSpecification',
            price: priceAmount,
            priceCurrency,
            referenceQuantity: {
              '@type': 'QuantitativeValue',
              value: 1,
              unitText: 'guest',
            },
          },
    availability: offerAvailability(packageItem, now),
    availabilityStarts: validDate(packageItem.availableDateFrom),
    availabilityEnds: validDate(packageItem.availableDateTo),
    eligibleQuantity: eligibleQuantity(packageItem),
    seller: { '@id': businessId },
    availableAtOrFrom: { '@id': businessId },
    // Package pages only output a Product when the package has a price.
    itemOffered: includeItemOffered && priceAmount !== null ? { '@id': productId } : null,
  });
};

const minimalBusiness = listing => {
  const canonicalUrl = listingUrl(listing);
  if (!canonicalUrl) return null;

  return compactObject({
    '@context': SCHEMA_CONTEXT,
    '@type': 'LocalBusiness',
    '@id': `${canonicalUrl}#business`,
    name: get(listing, 'attributes.title'),
    url: canonicalUrl,
    address: postalAddress(listing),
  });
};

const packageProperties = packageItem => {
  const properties = [
    rangeProperty('Duration', packageItem.days, 'days'),
    rangeProperty('Party size', packageItem.guests, 'guests'),
  ];
  const species = Array.isArray(packageItem.species)
    ? packageItem.species.filter(Boolean)
    : packageItem.species
    ? [packageItem.species]
    : [];
  if (species.length) properties.push(propertyValue('Species', species.join(', ')));

  const methods = [];
  if (get(packageItem, 'methods.allowsArchery', packageItem.allowsArchery)) methods.push('Archery');
  if (get(packageItem, 'methods.allowsFirearms', packageItem.allowsFirearms))
    methods.push('Firearms');
  if (methods.length) properties.push(propertyValue('Hunting methods', methods.join(', ')));

  const activities = Array.isArray(packageItem.activities)
    ? packageItem.activities.map(activityLabel).filter(Boolean)
    : [];
  if (activities.length) properties.push(propertyValue('Activities', activities.join(', ')));

  const packageLodgingStatus = lodgingStatus(packageItem);
  if (packageLodgingStatus) properties.push(propertyValue('Lodging', packageLodgingStatus));

  return properties.filter(Boolean);
};

export const buildListingStructuredData = ({ listing, intl, now }) => {
  const canonicalUrl = listingUrl(listing);
  if (!canonicalUrl) return [];

  const attributes = get(listing, 'attributes', {});
  const publicData = get(attributes, 'publicData', {});
  const title = attributes.title;
  const description = stripHtml(attributes.description);
  const activities = get(publicData, 'activities', {});
  const activityNames = Object.keys(activities)
    .map(activityLabel)
    .filter(Boolean);
  const acreage = get(publicData, 'location.acreage');
  const pageTitle = intl.formatMessage(
    { id: getListingSchemaTitleType(activities) },
    { title, usState: get(publicData, 'location.usState') }
  );
  const publishedPackages = get(publicData, 'packages', []).filter(item => item.isPublished);
  const offers = publishedPackages
    .map(packageItem => buildOffer({ listing, packageItem, intl, includeItemOffered: true, now }))
    .filter(Boolean);
  const additionalProperty = [];
  if (isFiniteNumber(acreage) && Number(acreage) > 0) {
    additionalProperty.push(propertyValue('Acreage', Number(acreage), { unitText: 'acres' }));
  }
  if (activityNames.length) {
    additionalProperty.push(propertyValue('Activities offered', activityNames.join(', ')));
  }

  const webpage = compactObject({
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    description,
    publisher: { '@id': ORGANIZATION_ID },
    mainEntity: { '@id': `${canonicalUrl}#business` },
  });
  const business = compactObject({
    '@context': SCHEMA_CONTEXT,
    '@type': 'LocalBusiness',
    '@id': `${canonicalUrl}#business`,
    name: title,
    description,
    url: canonicalUrl,
    image: imageUrls(getListingImages(listing), 5),
    address: postalAddress(listing),
    additionalProperty,
    makesOffer: offers,
  });

  return [webpage, business];
};

export const buildPackageStructuredData = ({ listing, package: packageItem, intl, now }) => {
  const canonicalUrl = packageUrl(listing, packageItem);
  const listingCanonicalUrl = listingUrl(listing);
  if (!canonicalUrl || !listingCanonicalUrl || !packageItem || !packageItem.isPublished) return [];

  const listingTitle = get(listing, 'attributes.title');
  const usState = get(listing, 'attributes.publicData.location.usState');
  const description = stripHtml(packageItem.description);
  const pageTitle = intl.formatMessage(
    { id: 'PackagePage.schemaTitle' },
    { packageTitle: packageItem.title, title: listingTitle, usState }
  );
  const packageImages = getPackageImages(listing, packageItem.id) || [];
  const images = packageImages.length ? packageImages : get(listing, 'images', []);
  const productId = `${canonicalUrl}#product`;
  const offer = buildOffer({ listing, packageItem, intl, requirePrice: true, now });

  const webpage = compactObject({
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    description,
    publisher: { '@id': ORGANIZATION_ID },
    mainEntity: offer ? { '@id': productId } : null,
  });
  if (!offer) return [webpage];

  const product = compactObject({
    '@context': SCHEMA_CONTEXT,
    '@type': 'Product',
    '@id': productId,
    url: canonicalUrl,
    sku: packageItem.id,
    name: packageItem.title,
    description,
    image: imageUrls(images),
    category: activityLabel(packageItem.activity),
    additionalProperty: packageProperties(packageItem),
    offers: offer,
    mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
  });

  return [webpage, product, minimalBusiness(listing)].filter(Boolean);
};
